/**
 * Every colour the portal uses, in one place, each one named.
 *
 * Nothing else may hold a colour literal: Tailwind classes, react-select's
 * emotion objects, inline styles and the stylesheets all read from here, and
 * no-rogue-colours.test.ts fails if a hex or rgb() appears anywhere else in the
 * app.
 *
 * Two groups:
 *   - design tokens — names and values from allenai/design.md
 *     earthranger/DESIGN.md. Reach for these first.
 *   - unlisted — in use but absent from that spec. Each is a candidate to be
 *     replaced by an official token; the prefix is what makes them countable.
 *
 * CommonJS on purpose: tailwind.config.js runs in node and requires this file,
 * so the palette cannot drift from the classes. src/styles/theme/colors.css mirrors it
 * as CSS variables for the stylesheets; a test keeps the two in step.
 */
const colors = {
  // ── EarthRanger design tokens ───────────────────────────────
  // From DESIGN.md — the names the designers use.
  'amber-1': '#362922',
  'amber-2': '#b83f00',
  'amber-3': '#e76826',
  'amber-4': '#ff803e',
  'amber-5': '#fd9964',
  'amber-6': '#fef7f4',
  'blue-1': '#0056c7',
  'blue-2': '#dde8f8',
  'blue-3': '#76b0ff',
  'blue-4': '#f4f7fd',
  'blue-5': '#2a394d',
  'blue-6': '#002960',
  'blue-7': '#3089ff',
  'brand-accent-green': '#0f865d',
  'brand-acorn-logo': '#008586',
  'green-1': '#006842',
  'green-10': '#78d811',
  'green-11': '#a1e458',
  'green-12': '#0f865d',
  'green-2': '#83af52',
  'green-3': '#00882e',
  'green-4': '#53aa71',
  'green-5': '#004e26',
  'green-6': '#f5f7f3',
  'green-7': '#0fcb8c',
  'green-8': '#53693a',
  'green-9': '#488a00',
  'greyscale-0': '#000000',
  'greyscale-1': '#111111',
  'greyscale-10': '#f3f3f3',
  'greyscale-11': '#f9f9f9',
  'greyscale-2': '#222222',
  'greyscale-3': '#333333',
  'greyscale-4': '#444444',
  'greyscale-5': '#63666a',
  'greyscale-6': '#767676',
  'greyscale-7': '#888b8d',
  'greyscale-8': '#b1b3b3',
  'greyscale-9': '#dddddd',
  'greyscale-off-white': '#faf2e9',
  'indigo-1': '#331878',
  'indigo-2': '#3e35a3',
  'indigo-3': '#5c4fe7',
  'indigo-4': '#57528c',
  'indigo-5': '#867ede',
  'indigo-6': '#f5f5fa',
  'magenta-1': '#8b0f56',
  'magenta-2': '#b62879',
  'magenta-3': '#ed3ea2',
  'magenta-4': '#e76eb3',
  'purple-1': '#71038e',
  'purple-2': '#a100cb',
  'purple-3': '#cc74e6',
  'red-1': '#3e2125',
  'red-2': '#8c1700',
  'red-3': '#d0031b',
  'red-4': '#f23b0e',
  'red-5': '#f06374',
  'red-6': '#fdf2f4',
  'teal-1': '#107283',
  'teal-2': '#62bfd0',
  'teal-3': '#008586',
  'teal-4': '#00958f',
  'teal-5': '#00d0c8',
  'teal-ai2-dark-teal': '#0a3235',
  'teal-ai2-extra-dark-teal': '#032629',
  'teal-t10': '#e7eeee',
  'teal-t100': '#105257',
  'teal-t20': '#cfdcdd',
  'teal-t30': '#b7cbcd',
  'teal-t40': '#9fbabc',
  'teal-t50': '#87a8ab',
  'teal-t60': '#70979a',
  'teal-t70': '#588689',
  'teal-t80': '#407579',
  'teal-t90': '#27545f',
  'ui-background-elevated-component': '#ffffff',
  'ui-background-primary-background': '#ffffff',
  'ui-background-secondary-background': '#faf2e9',
  'ui-background-tertiary-background': '#e7eeee',
  'ui-cta-primary': '#0f865d',
  'ui-cta-secondary-blue': '#cfdcdd',
  'ui-data-categorical-category-1': '#3e35a3',
  'ui-data-categorical-category-10': '#ed3ea2',
  'ui-data-categorical-category-2': '#488a00',
  'ui-data-categorical-category-3': '#00958f',
  'ui-data-categorical-category-4': '#b62879',
  'ui-data-categorical-category-5': '#c6880c',
  'ui-data-categorical-category-6': '#00d0c8',
  'ui-data-categorical-category-7': '#78d811',
  'ui-data-categorical-category-8': '#5c4fe7',
  'ui-data-categorical-category-9': '#f4db20',
  'ui-data-sequential-sequential-1': '#f4db20',
  'ui-data-sequential-sequential-2': '#ffb700',
  'ui-data-sequential-sequential-3': '#ed3ea2',
  'ui-data-sequential-sequential-4': '#a100cb',
  'ui-data-sequential-sequential-5': '#5c4fe7',
  'ui-data-sequential-sequential-6': '#0056c7',
  'ui-data-sequential-sequential-7': '#00882e',
  'ui-data-sequential-sequential-8': '#78d811',
  'ui-data-tracks-track-1': '#3e35a3',
  'ui-data-tracks-track-10': '#f23b0e',
  'ui-data-tracks-track-2': '#b62879',
  'ui-data-tracks-track-3': '#ff803e',
  'ui-data-tracks-track-4': '#ed3ea2',
  'ui-data-tracks-track-5': '#3089ff',
  'ui-data-tracks-track-6': '#8c1700',
  'ui-data-tracks-track-7': '#a100cb',
  'ui-data-tracks-track-8': '#004e26',
  'ui-data-tracks-track-9': '#002960',
  'ui-divider-lines': '#dddddd',
  'ui-event-priority-amber': '#e76826',
  'ui-event-priority-green': '#006842',
  'ui-event-priority-grey': '#888b8d',
  'ui-event-priority-red': '#d0031b',
  'ui-field-outline': '#b1b3b3',
  'ui-icons': '#767676',
  'ui-patrol-status-active': '#3e35a3',
  'ui-patrol-status-done-mobile': '#767676',
  'ui-patrol-status-overdue': '#b62879',
  'ui-patrol-status-scheduled': '#107283',
  'ui-patrol-status-track-line-mobile': '#e76826',
  'ui-status-bg-active-bg': '#f5f5fa',
  'ui-status-bg-draft-bg': '#dde8f8',
  'ui-status-bg-error-bg': '#fdf2f4',
  'ui-status-bg-pending-sync-bg': '#f3f3f3',
  'ui-status-bg-synced-bg': '#f5f7f3',
  'ui-status-bg-warning-bg': '#fef7f4',
  'ui-status-disabled': '#767676',
  'ui-status-error-destructive': '#d0031b',
  'ui-status-ready': '#ffb700',
  'ui-status-running': '#0056c7',
  'ui-status-success': '#0f865d',
  'ui-status-warning': '#e76826',
  'ui-text-disabled': '#767676',
  'ui-text-form-labels': '#105257',
  'ui-text-primary-text': '#0a3235',
  'ui-text-primary-text-reversed': '#ffffff',
  'ui-text-secondary-text': '#105257',
  'ui-text-secondary-text-reversed': '#faf2e9',
  'ui-text-tabs': '#105257',
  white: '#ffffff',
  'yellow-1': '#9d6900',
  'yellow-2': '#c6880c',
  'yellow-3': '#ffb700',
  'yellow-4': '#f4db20',

  // ── Unlisted ────────────────────────────────────────────────
  // No token in DESIGN.md for these values (yet). None left: keep it that way.
};

// A palette colour made see-through, for shadows, dimmers and overlays.
// DESIGN.md has no see-through tokens, so these start from an official colour
// and only add the opacity: seeThrough('greyscale-0', 0.25).
const seeThrough = (name, opacity) => {
  const hex = colors[name];
  if (!hex || !/^#[0-9a-f]{6}$/.test(hex))
    throw new Error(`seeThrough: no solid palette colour named "${name}"`);
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

module.exports = { colors, seeThrough };
